import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  useNotificationPreferences,
  useUpdatePreferences,
} from '@/api/notifications';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { resolveNotificationLink } from '@/lib/notification-link';
import { Bell, CheckCheck, Search, Settings } from 'lucide-react';
import type { NotificationType, Notification } from '@/types';
import { READ_FILTERS, TYPE_OPTIONS, PREF_FIELDS } from './_constants';
import { NotificationCard } from './_components/notification-card';

type Tab = 'notifications' | 'preferences';

export function NotificationsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('notifications');
  const [isRead, setIsRead] = useState<boolean | undefined>(false);
  const [type, setType] = useState<NotificationType | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search.trim(), 300);

  const {
    data: notificationsRes,
    isLoading,
    error,
  } = useNotifications({
    is_read: isRead,
    notification_type: type,
    search: debouncedSearch || undefined,
    page,
    per_page: 10,
  });
  const notifications = notificationsRes?.data ?? [];
  const pagination = notificationsRes?.meta?.pagination;

  function handleNotificationClick(notif: Notification) {
    if (!notif.is_read) markRead.mutate(notif.id);
    const link = resolveNotificationLink(notif.resource_type, notif.resource_id);
    if (link) navigate(link);
  }

  const unreadCount = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const { data: preferences, isLoading: prefsLoading } = useNotificationPreferences();
  const updatePrefs = useUpdatePreferences();

  function handleFilterChange(readValue: boolean | undefined) {
    setIsRead(readValue);
    setPage(1);
  }

  function handleTypeChange(value: NotificationType | undefined) {
    setType(value);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Notificaciones</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gestiona tus notificaciones y preferencias de correo.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 rounded-lg border border-border bg-muted/50 p-1">
        {(['notifications', 'preferences'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t === 'notifications' ? (
              <Bell className="h-4 w-4" />
            ) : (
              <Settings className="h-4 w-4" />
            )}
            {t === 'notifications' ? 'Notificaciones' : 'Preferencias'}
            {t === 'notifications' && unreadCount.data ? (
              <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold text-primary-foreground">
                {unreadCount.data}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* NOTIFICATIONS TAB */}
      {tab === 'notifications' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por consecutivo (ICN-…), título o contenido"
              className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-1 rounded-lg border border-border bg-muted/50 p-1">
              {READ_FILTERS.map((f) => (
                <button
                  key={f.label}
                  onClick={() => handleFilterChange(f.value)}
                  className={cn(
                    'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                    isRead === f.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={type ?? ''}
                onChange={(e) =>
                  handleTypeChange((e.target.value || undefined) as NotificationType | undefined)
                }
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending || !unreadCount.data}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-card-foreground transition-colors hover:bg-accent disabled:opacity-40"
              >
                <CheckCheck className="h-4 w-4" />
                Marcar todas como leídas
              </button>
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage message="No se pudieron cargar las notificaciones." />
          ) : notifications.length === 0 ? (
            <EmptyState
              title="Sin notificaciones"
              description={
                isRead === false && type === undefined && debouncedSearch === ''
                  ? 'Estás al día: no tienes notificaciones sin leer.'
                  : 'No tienes notificaciones con los filtros seleccionados.'
              }
            />
          ) : (
            <>
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <NotificationCard
                    key={notif.id}
                    notif={notif}
                    onClick={() => handleNotificationClick(notif)}
                    onMarkRead={() => markRead.mutate(notif.id)}
                    isMarkingRead={markRead.isPending}
                  />
                ))}
              </div>
              {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
            </>
          )}
        </div>
      )}

      {/* PREFERENCES TAB */}
      {tab === 'preferences' && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-card-foreground">Preferencias de Correo</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Selecciona los tipos de notificación que deseas recibir por correo electrónico.
          </p>
          {prefsLoading ? (
            <div className="mt-6">
              <LoadingSpinner />
            </div>
          ) : !preferences ? (
            <ErrorMessage message="No se pudieron cargar las preferencias." />
          ) : (
            <div className="mt-6 space-y-4">
              {PREF_FIELDS.map((field) => (
                <label key={field.key} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={Boolean(preferences[field.key])}
                    onChange={(e) => updatePrefs.mutate({ [field.key]: e.target.checked })}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-card-foreground">{field.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
